import React, { useState } from "react";

import {
  Box,
  Grow,
  Icon,
  Paper,
  Button,
  ClickAwayListener,
  PopperPlacementType,
  Popper,
} from "@mui/material";
import { Editor } from "slate";
import { useSlate } from "slate-react";
import { Close, Done } from "@mui/icons-material";

import { classes } from "../../styles";
import { CustomEditor } from "../../custom-types";

interface IColorProps {
  id: number;
  value: string;
}

const colorOptions: IColorProps[] = [
  { id: 1, value: "rgb(244, 67, 54)" },
  { id: 2, value: "rgb(233, 30, 99)" },
  { id: 3, value: "rgb(156, 39, 176)" },
  { id: 4, value: "rgb(103, 58, 183)" },
  { id: 5, value: "rgb(63, 81, 181)" },
  { id: 6, value: "rgb(33, 150, 243)" },
  { id: 7, value: "rgb(3, 169, 244)" },
  { id: 8, value: "rgb(0, 188, 212)" },
  { id: 9, value: "#009688" },
  { id: 10, value: "#4caf50" },
  { id: 11, value: "#8bc34a" },
  { id: 12, value: "#cddc39" },
  { id: 13, value: "#ffeb3b" },
  { id: 14, value: "#ffc107" },
  { id: 15, value: "#ff9800" },
  { id: 16, value: "#ff5722" },
];

interface IProps {
  format: string;
  icon: string;
  disabled?: boolean;
}

export const ColorButton: React.FC<IProps> = ({
  format,
  icon,
  disabled = false,
}) => {
  const editor = useSlate();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [placement, setPlacement] = useState<PopperPlacementType>();
  const [isMouseEnter, setIsMouseEnter] = useState(false);

  const isMarkActive = (editor: CustomEditor, format: string) => {
    const marks = Editor.marks(editor);

    if (marks) {
      return marks[format] ? marks[format] : false;
    }
  };

  const handleMarkButton = (
    editor: CustomEditor,
    format: string,
    colorSelected: string
  ) => {
    const isActive = isMarkActive(editor, format);

    if (isActive && String(isActive) === colorSelected) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, colorSelected);
      setOpen(false);
    }
  };

  const handleToggle =
    (newPlacement: PopperPlacementType) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      setOpen((prev) => placement !== newPlacement || !prev);
      setPlacement(newPlacement);
    };

  const handleClose = () => setOpen(false);

  return (
    <>
      <Button
        size="small"
        disabled={disabled}
        variant="contained"
        disableElevation={!isMarkActive(editor, format)}
        color={isMarkActive(editor, format) ? "inherit" : "primary"}
        sx={classes.button}
        onClick={handleToggle("bottom-start")}
      >
        <Icon
          fontSize="small"
          sx={classes.icon}
          style={{
            color:
              isMarkActive(editor, format) !== false &&
              isMarkActive(editor, format) !== undefined
                ? String(isMarkActive(editor, format))
                : "#fff",
          }}
        >
          {icon}
        </Icon>
      </Button>
      <Popper
        style={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorEl}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <Box sx={classes.boxColor}>
                  {colorOptions.length > 0
                    ? colorOptions.map((color) => (
                        <Button
                          key={color.id}
                          sx={classes.colorButton}
                          style={{
                            backgroundColor: color.value,
                          }}
                          onMouseEnter={() => setIsMouseEnter(true)}
                          onMouseLeave={() => setIsMouseEnter(false)}
                          onClick={(event) => {
                            event.preventDefault();
                            handleMarkButton(editor, format, color.value);
                          }}
                        >
                          {isMarkActive(editor, format) === color.value &&
                            (!isMouseEnter ? (
                              <Done
                                sx={classes.icon}
                                style={{ fontSize: "18px" }}
                              />
                            ) : (
                              <Close
                                sx={classes.icon}
                                style={{ fontSize: "18px" }}
                              />
                            ))}
                        </Button>
                      ))
                    : "Não há cores"}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
